import React, { useMemo } from "react";
import Modal from "../../../common/Modal";
import { useAuth } from "../../../../store/hooks/useAuth";
import type { MyExit } from "../../Utils/types";
import {
  extractFilenameFromUrl,
  pickAttachmentLinkFromExit,
  pickAttachmentNameFromExit,
} from "../../Utils/attachmentUtils";
import {
  asExitRecord,
  exitListStatusLabel,
  functionalManagerDisplay,
  lastWorkingDisplay,
  reasonDisplay,
  submittedOnDisplay,
} from "../../Utils/exitRecordDisplay";
import { formatExitDateDisplay } from "../../Utils/exitDateUtils";
import NoticePeriodCard from "./NoticePeriodCard";

export interface MyExitViewModalProps {
  exit: MyExit | null;
  onClose: () => void;
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  const isEmpty =
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "");
  const displayValue = isEmpty ? "—" : value;

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
        {label}
      </span>
      <div className="text-[14px] font-medium text-slate-800 whitespace-pre-wrap break-words leading-snug">
        {displayValue}
      </div>
    </div>
  );
}

const MyExitViewModal: React.FC<MyExitViewModalProps> = ({ exit, onClose }) => {
  const { user } = useAuth();
  const row = exit ? asExitRecord(exit) : null;
  const attachmentHref =
    exit && row ? pickAttachmentLinkFromExit(row) : undefined;
  const rawAttachmentName =
    exit && row ? pickAttachmentNameFromExit(row) : undefined;
  const attachmentName =
    rawAttachmentName ||
    (attachmentHref ? extractFilenameFromUrl(attachmentHref) : "Document");
  const emp = exit?.employee;

  /** my-exit often omits FM on `employee`; session user (same as profile) usually has it. */
  const functionalManagerValue = useMemo(() => {
    if (!exit) return "—";
    const fromPayload = functionalManagerDisplay(exit);
    if (fromPayload !== "—") return fromPayload;
    const fromSession = user?.functionalManagerName?.trim();
    return fromSession || "—";
  }, [exit, user?.functionalManagerName]);

  return (
    <Modal
      isOpen={!!exit}
      onClose={onClose}
      title="Resignation details"
      size="4xl"
    >
      {exit && (
        <div className="w-full pb-2 pt-1">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
            <div className="space-y-5">
              <DetailRow label="Employee Name" value={emp?.fullName} />
              <DetailRow label="Employee Id" value={emp?.employeeCode} />
              <DetailRow label="Email" value={emp?.workEmail} />
              <DetailRow label="Department" value={emp?.department} />
              <DetailRow label="Designation" value={emp?.designation} />
              <DetailRow
                label="Reporting manager"
                value={
                  emp?.reportingManagerName &&
                  emp.reportingManagerName.trim()
                    ? emp.reportingManagerName
                    : "—"
                }
              />
              <DetailRow
                label="Functional manager"
                value={functionalManagerValue}
              />
              {attachmentHref ? (
                <div className="space-y-2">
                  <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-0">
                    Supporting documents
                  </h3>
                  <ul className="list-disc pl-5 text-[14px] text-primary-600 space-y-1 m-0">
                    <li>
                      <a
                        href={attachmentHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline font-semibold"
                      >
                        {attachmentName}
                      </a>
                    </li>
                  </ul>
                </div>
              ) : null}
            </div>

            <div className="space-y-5">
              <DetailRow
                label="Approval Status"
                value={exitListStatusLabel(exit)}
              />
              <DetailRow
                label="Exit status"
                value={exit.exitStatus ?? exit.status ?? "—"}
              />
              {/* <DetailRow
                label="Resignation date"
                value={
                  exit.resignationDate
                    ? formatExitDateDisplay(exit.resignationDate)
                    : "—"
                }
              /> */}
              <DetailRow
                label="Requested Last working date"
                value={lastWorkingDisplay(exit)}
              />
              {exit.managerSuggestedLastWorkingDay ? (
                <DetailRow
                  label="Manager suggested LWD"
                  value={formatExitDateDisplay(
                    exit.managerSuggestedLastWorkingDay,
                  )}
                />
              ) : null}
              {/* {typeof exit.noticePeriodDays === "number" ? (
                <DetailRow
                  label="Notice period (days)"
                  value={String(exit.noticePeriodDays)}
                />
              ) : null} */}
              <DetailRow label="Reason" value={reasonDisplay(exit)} />
              {exit.comments ? (
                <DetailRow label="Comments" value={exit.comments} />
              ) : null}
              <DetailRow
                label="Submitted on"
                value={submittedOnDisplay(exit)}
              />
            </div>
          </div>

          {exit.noticePeriodTracking &&
          typeof exit.noticePeriodTracking === "object" ? (
            <div className="mt-6">
              <NoticePeriodCard tracking={exit.noticePeriodTracking} />
            </div>
          ) : null}

          {/* {exit.workflow ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-3">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Workflow
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
                <DetailRow
                  label="Label"
                  value={exit.workflow.workflowStatusLabel ?? "—"}
                />
                <DetailRow
                  label="Workflow status"
                  value={exit.workflow.workflowStatus ?? "—"}
                />
                <DetailRow
                  label="Approval instance"
                  value={exit.workflow.approvalInstanceId ?? "—"}
                />
                <DetailRow
                  label="Flags"
                  value={
                    [
                      exit.workflow.isCompleted ? "Completed" : null,
                      exit.workflow.isRejected ? "Rejected" : null,
                      exit.workflow.isCancelled ? "Cancelled" : null,
                    ]
                      .filter(Boolean)
                      .join(", ") || "—"
                  }
                />
              </div>
            </div>
          ) : null} */}
        </div>
      )}
    </Modal>
  );
};

export default MyExitViewModal;
